import { ElementRef, PropsWithChildren, useReducer, useRef, useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { css } from '@emotion/react';
import useResizeObserver from '@react-hook/resize-observer';
import composeRefs from '@seznam/compose-react-refs';
import { AnimatePresence, motion } from 'motion/react';
import { transparentize } from '@frontend/colors';
import { useEventListener } from '@frontend/event';
import { useTranslation } from '@frontend/i18n';
import { debounce } from '@frontend/timer';
import { theme } from '@frontend/theme';
import { useAlert } from '@frontend/design-system';
import type { DragDropState } from '../../hooks/scheduler-v3/use-appointment-drag-drop';
import { isUUID } from '../../views/Calendar/utils';
import { createColumnId } from './calendar-view-v3/helper';
import { PROVIDER_LANE_WIDTH } from './constants';
import { useCalendarView, useCalendarViewProps } from './providers';
import { initialTimelineState, timelineReducer } from './reducers';
import { TimeTooltip } from './time-tooltip';
import { checkIntersection, getTimestamp, zIndex } from './utils';
import { useZoomSafeContext } from './zoom/zoom-context';

type EventTimelineWrapper = {
  columnEntityName?: string;
  columnEntityId?: string;
  locationId?: string;
  calendarDateValue?: string;
  isRegisterResizeObserver?: boolean;
  dragDropState?: DragDropState;
  isSourceColumn?: boolean;
  isOverColumn?: boolean;
  hasPendingDrop?: boolean;
  isUnassignedColumn?: boolean;
};

export const EventTimelineWrapper = ({
  children,
  columnEntityName,
  columnEntityId,
  locationId,
  calendarDateValue,
  isRegisterResizeObserver,
  dragDropState,
  isSourceColumn = false,
  isOverColumn = false,
  hasPendingDrop = false,
  isUnassignedColumn = false,
}: PropsWithChildren<EventTimelineWrapper>) => {
  const { pixelsPerMinute } = useZoomSafeContext();
  const { startHour, endHour, onEventCreate, shouldAllowEventCreation } = useCalendarViewProps();
  const {
    setVisibleLaneCountBasedOnTotalWidth,
    containerState: { clientWidth },
  } = useCalendarView(['setVisibleLaneCountBasedOnTotalWidth', 'containerState']);
  const [{ isActivated: isEventActivated, eventStartX, eventStartTime, eventEndX, eventEndTime }, dispatch] =
    useReducer(timelineReducer, initialTimelineState);

  // NOTE: Hiding the intersection restriction for now until we make decision on how to handle different use cases
  const shouldCheckIntersection = false;

  const isActivated = isEventActivated;
  const [isIntersecting, setIntersecting] = useState(false);
  const alert = useAlert();
  const { t } = useTranslation('scheduleCalendarViewMain');

  const timelineRef = useRef<ElementRef<'section'>>(null);
  const eventBoxRef = useRef<ElementRef<'div'>>(null);

  const { setNodeRef } = useDroppable({ id: createColumnId(locationId, columnEntityId) });

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!shouldAllowEventCreation) return;
    const shouldRound = !e.shiftKey;
    const timestamp = getTimestamp({
      event: e,
      element: timelineRef.current,
      nearestQuarter: shouldRound,
      startHour,
      endHour,
      nearestIncrement: 10,
      pixelsPerMinute,
    });

    if (!timestamp) return;

    const { formattedTime, totalMinutes } = timestamp;

    dispatch({
      type: 'ACTIVATE',
      payload: {
        eventStartX: totalMinutes,
        eventEndX: totalMinutes,
        eventStartTime: formattedTime,
        eventEndTime: formattedTime,
      },
    });
  };

  function handleMouseMove(e: MouseEvent) {
    if (!eventBoxRef.current || !timelineRef.current || !shouldAllowEventCreation) return;

    if (shouldCheckIntersection) {
      setIntersecting(
        checkIntersection({
          targetElement: eventBoxRef.current,
          containerElement: timelineRef.current,
        })
      );
    }

    const shouldRound = !e.shiftKey;

    const timestamp = getTimestamp({
      event: e,
      element: timelineRef.current,
      nearestQuarter: shouldRound,
      startHour,
      endHour,
      nearestIncrement: 10,
      pixelsPerMinute,
    });

    if (!timestamp) return;

    const { formattedTime, totalMinutes } = timestamp;

    dispatch({
      type: 'UPDATE_POSITION',
      payload: {
        eventEndX: totalMinutes,
        eventEndTime: formattedTime,
      },
    });
  }

  const handleMouseUp = () => {
    if (!!columnEntityId && !isUUID(columnEntityId ?? '') && shouldAllowEventCreation) {
      !isIntersecting && alert.error(t('Event cannot be created. This provider does not have a provider ID'));
    } else {
      onEventCreate?.(
        eventStartX > eventEndX ? eventEndTime : eventStartTime,
        eventStartX > eventEndX ? eventStartTime : eventEndTime,
        columnEntityName,
        columnEntityId,
        !isIntersecting,
        locationId ?? '',
        calendarDateValue ?? ''
      );
    }

    dispatch({
      type: 'DEACTIVATE',
    });
  };

  useEventListener('mousemove', handleMouseMove, isActivated);
  useEventListener('mouseup', handleMouseUp, isActivated);

  // Observe only provider timeline for resize to update event-cards width (and placement in event group)
  useResizeObserver(
    isRegisterResizeObserver ? timelineRef : null,
    debounce((entry) => {
      const measuredWidth = Math.round(entry.contentRect.width);

      if (!measuredWidth || Number.isNaN(measuredWidth)) {
        return;
      }

      // Ignore transient samples until the calendar container has settled after a cross-display resize.
      if (clientWidth > 0 && measuredWidth > clientWidth) {
        return;
      }

      setVisibleLaneCountBasedOnTotalWidth(measuredWidth);
    }, 100)
  );

  const disableEventCreate = isIntersecting || (!!columnEntityId && !isUUID(columnEntityId ?? ''));

  // Determine drag-related visual state
  const isDragging = dragDropState?.isDragging;
  const showDragSourcePlaceholder = (isDragging || hasPendingDrop) && isSourceColumn;
  const showDropTargetPlaceholder = (isDragging || hasPendingDrop) && isOverColumn && !isUnassignedColumn;
  const draggedEventPosition = dragDropState?.draggedEventPosition;
  const dropTargetTop = dragDropState?.dropTargetTop;

  return (
    <section ref={composeRefs(timelineRef, setNodeRef)} onMouseDown={handleMouseDown} css={styles.wrapper}>
      {children}
      {/* Show placeholder at the original position when dragging */}
      <AnimatePresence>
        {showDragSourcePlaceholder && draggedEventPosition && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            css={styles.dragSourcePlaceholder({
              top: draggedEventPosition.top,
              height: draggedEventPosition.height,
              width: draggedEventPosition.width,
              left: draggedEventPosition.left,
            })}
          />
        )}
      </AnimatePresence>
      {/* Show drop target at the current drag position */}
      <AnimatePresence>
        {showDropTargetPlaceholder && draggedEventPosition && dropTargetTop !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            css={styles.dropTargetPlaceholder({
              top: dropTargetTop || 0,
              height: draggedEventPosition.height,
            })}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {isActivated && (
          <motion.div
            ref={eventBoxRef}
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              background: disableEventCreate ? transparentize(0.2, theme.colors.critical20) : theme.colors.primary20,
              border: `1px dashed ${
                disableEventCreate ? transparentize(0.2, theme.colors.critical50) : theme.colors.primary40
              }`,
            }}
            exit={{
              opacity: 0,
              scale: 1.1,
            }}
            transition={{ duration: 0.3 }}
            css={styles.creatorCard({
              top: Math.min(eventStartX, eventEndX),
              height: Math.abs(eventStartX - eventEndX),
              isIntersecting,
            })}
          >
            <motion.div css={styles.tooltipContainer}>
              <TimeTooltip>
                {eventEndX - eventStartX < 0
                  ? `${eventEndTime} - ${eventStartTime}`
                  : `${eventStartTime} - ${eventEndTime}`}
              </TimeTooltip>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

const verticalGridLineStyle = css({
  content: '""',
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: '1px',
  backgroundColor: theme.colors.neutral20,
  zIndex: zIndex.providerColumnVerticalLines,
  pointerEvents: 'none',
});

const styles = {
  wrapper: css({
    flex: `1 0 ${PROVIDER_LANE_WIDTH}px`,
    position: 'relative',
    padding: theme.spacing(0, 1),
    '&::before': [verticalGridLineStyle, { left: 0 }],
    '&:nth-last-of-type(1)::after': [verticalGridLineStyle, { right: 0 }],
  }),
  tooltipContainer: css({
    pointerEvents: 'none',
    position: 'absolute',
    left: '50%',
    top: '-60px',
    transform: 'translateX(-50%)',
    width: 'auto',
  }),
  creatorCard: ({ top, height, isIntersecting }: { top: number; height: number; isIntersecting: boolean }) =>
    css({
      position: 'absolute',
      zIndex: zIndex.eventCreatorCard,
      borderRadius: theme.borderRadius.small,
      userSelect: 'none',
      background: isIntersecting ? theme.colors.critical20 : theme.colors.primary20,
      border: `1px solid ${isIntersecting ? theme.colors.critical50 : theme.colors.primary40}`,
      top,
      height,
      left: 0,
      width: '100%',
    }),
  dragSourcePlaceholder: ({ top, height, width, left }: { top: number; height: number; width: string; left: string }) =>
    css({
      position: 'absolute',
      top,
      height,
      width,
      left,
      borderRadius: theme.borderRadius.small,
      border: `1px dashed ${theme.colors.primary50}`,
      boxShadow: theme.shadows.light,
      pointerEvents: 'none',
      zIndex: zIndex.eventCardDefault - 1,
    }),
  dropTargetPlaceholder: ({ top, height }: { top: number; height: number }) =>
    css({
      position: 'absolute',
      top,
      height,
      left: 0,
      right: 0,
      borderRadius: theme.borderRadius.small,
      border: `1px solid ${theme.colors.primary20}`,
      backgroundColor: theme.colors.primary10,
      pointerEvents: 'none',
      zIndex: zIndex.eventCardDefault + 1,
    }),
};
